import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { exportAssessmentCompanies } from '@/api/risk';
import type { UpdateAssessmentBlueprintPayload } from '@/api/risk/types';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs from '@/components/shared/PageTabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { AddCompaniesToAssessmentDialog } from '../../../../-components/AddCompaniesToAssessmentDialog';
import { AssessmentCompaniesTab } from '../../../../-components/AssessmentCompaniesTab';
import { AssessmentDetailsTab } from '../../../../-components/AssessmentDetailsTab';
import { AssessmentPrioritizationTab } from '../../../../-components/AssessmentPrioritizationTab';
import { useAssessmentBlueprintMutations } from '../../../../-hooks/useAssessmentBlueprintMutations';
import { useAssessmentCompanies } from '../../../../-hooks/useAssessmentCompanies';
import { useAssessmentDetail } from '../../../../-hooks/useAssessmentDetail';
import { useAssessmentPrioritization } from '../../../../-hooks/useAssessmentPrioritization';
import { useFormulaOptions } from '../../../../-hooks/useFormulaOptions';

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(detail)/assessments/$id/',
)({
  validateSearch: z.object({ tab: z.string().optional() }),
  component: AssessmentDetailPage,
});

function AssessmentDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch() as { tab?: string };
  const { getTranslation: t } = useTranslator({ page: 'riskAssessments' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });

  const detail = useAssessmentDetail(id);
  const companies = useAssessmentCompanies(id);
  const prioritization = useAssessmentPrioritization(id);
  const { updateMutation } = useAssessmentBlueprintMutations();
  const blueprint = detail.data;
  const formulaLocked = (blueprint?.assessmentCount ?? 0) > 0;
  const { data: formulaOptions } = useFormulaOptions(!formulaLocked);

  // The PATCH route requires the full { name, assessmentFormulaId } body (+
  // allowlisted goal/tags) — it rejects partial patches. Merge the one edited
  // field into the current blueprint so every inline edit sends a complete,
  // schema-valid body (goal/tags sent as null to clear).
  const buildUpdatePayload = (
    change: Partial<UpdateAssessmentBlueprintPayload>,
  ): UpdateAssessmentBlueprintPayload => {
    const goal = change.goal ?? blueprint?.goal ?? null;
    const tags = change.tags ?? blueprint?.tags ?? [];
    return {
      name: change.name ?? blueprint?.name ?? '',
      assessmentFormulaId:
        (change.assessmentFormulaId ?? blueprint?.assessmentFormulaId) || '',
      goal: goal?.trim() ? goal : null,
      tags: tags.length ? tags : null,
    };
  };

  const perms = usePermissions().risk;

  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [addCompaniesOpen, setAddCompaniesOpen] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [exportCompanyIds, setExportCompanyIds] = useState<
    string[] | undefined
  >(undefined);

  const exportMutation = useMutation({
    mutationFn: async (companyIds?: string[]) => {
      try {
        await exportAssessmentCompanies(id, companyIds);
        toast.success(t('companies.export.exportStarted'));
      } catch (e) {
        toast.error((e as Error).message);
        throw e;
      }
    },
  });

  const handleExport = (companyIds?: string[]) => {
    setExportCompanyIds(companyIds);
    setExportConfirmOpen(true);
  };

  const handleExportConfirm = async () => {
    await exportMutation.mutateAsync(exportCompanyIds);
    setExportConfirmOpen(false);
  };

  const activeTab = tab ?? 'details';

  if (detail.isLoading) {
    return (
      <PageContainer className='gap-4'>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className='h-32 w-full' />
        ))}
      </PageContainer>
    );
  }

  return (
    <div className='flex flex-col h-full overflow-hidden'>
      <PageTabs
        moreLabel={tShared('moreTabs')}
        tabs={[
          { key: 'details', label: t('detail.tabs.details') },
          {
            key: 'companies',
            label: t('detail.tabs.companies'),
            ...(detail.data ? { count: detail.data.assessmentCount } : {}),
          },
          { key: 'prioritization', label: t('detail.tabs.prioritization') },
        ]}
        defaultTab='details'
        param='tab'
      />

      <div className='flex-1 min-h-0 overflow-hidden'>
        {activeTab === 'details' && (
          <PageContainer>
            {blueprint ? (
              <AssessmentDetailsTab
                blueprint={blueprint}
                {...(formulaOptions !== undefined ? { formulaOptions } : {})}
                {...(perms.canEdit
                  ? {
                      onUpdate: {
                        name: (v: string) =>
                          updateMutation.mutate({
                            id,
                            payload: buildUpdatePayload({ name: v.trim() }),
                          }),
                        goal: v =>
                          updateMutation.mutate({
                            id,
                            payload: buildUpdatePayload({ goal: v }),
                          }),
                        tags: v =>
                          updateMutation.mutate({
                            id,
                            payload: buildUpdatePayload({ tags: v }),
                          }),
                        assessmentFormulaId: (v: string | null) => {
                          if (!v) return;
                          updateMutation.mutate({
                            id,
                            payload: buildUpdatePayload({
                              assessmentFormulaId: v,
                            }),
                          });
                        },
                      },
                    }
                  : {})}
              />
            ) : (
              <div className='flex flex-col gap-4'>
                {[1, 2].map(i => (
                  <Skeleton key={i} className='h-48 w-full' />
                ))}
              </div>
            )}
          </PageContainer>
        )}
        {activeTab === 'companies' && (
          <PageContainer className='h-full overflow-hidden'>
            <AssessmentCompaniesTab
              blueprintId={id}
              rows={companies.rows}
              total={companies.total}
              isLoading={companies.isLoading}
              sorting={companies.sorting}
              filters={companies.filters}
              pagination={companies.pagination}
              onSortingChange={companies.onSortingChange}
              onFiltersChange={companies.onFiltersChange}
              onPaginationChange={companies.onPaginationChange}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              {...(perms.canCreate
                ? {
                    onRecalculate: (ids?: string[]) =>
                      companies.recalculateMutation.mutate(ids),
                    onAddCompanies: () => setAddCompaniesOpen(true),
                    onExport: handleExport,
                  }
                : {})}
              isRecalculating={companies.recalculateMutation.isPending}
              isExporting={exportMutation.isPending}
              isArchived={!!blueprint?.isArchived}
            />
          </PageContainer>
        )}
        {activeTab === 'prioritization' && (
          // Chart + table stack taller than the viewport, and the tab wrapper
          // above is `overflow-hidden`, so this container has to be the
          // scroller — the default `overflow-hidden` clipped the table at
          // whatever row fell below the fold.
          <PageContainer className='h-full overflow-y-auto'>
            <AssessmentPrioritizationTab
              items={prioritization.data?.data ?? []}
              isLoading={prioritization.isLoading}
            />
          </PageContainer>
        )}
      </div>

      {perms.canCreate && (
        <AddCompaniesToAssessmentDialog
          blueprintId={id}
          {...(blueprint?.name ? { blueprintName: blueprint.name } : {})}
          open={addCompaniesOpen}
          onOpenChange={setAddCompaniesOpen}
        />
      )}

      {/* Export confirmation dialog */}
      <AlertDialog
        open={exportConfirmOpen}
        onOpenChange={open => {
          if (!open) setExportConfirmOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('list.export.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {exportCompanyIds?.length
                ? t('companies.export.confirmDescriptionSelected')
                : t('companies.export.confirmDescriptionAll')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex justify-end gap-2 pt-2'>
            <AlertDialogCancel
              disabled={exportMutation.isPending}
              onClick={() => setExportConfirmOpen(false)}
            >
              {t('dialog.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant='default'
              onClick={() => void handleExportConfirm()}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending
                ? t('list.export.exporting')
                : t('list.export.confirm')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
